<template>
  <ItemNotFoundState v-if="objectNotFound" :objectName="'Typ oznámení'">
    <template #backButton>
        <BackButton :target="{name: 'announcementTypes'}">Typy oznámení</BackButton>
    </template>
  </ItemNotFoundState>
  <Loading v-else-if="!didLoadData"/>
  <div v-else>
    <BackButton :target="{name: 'announcementTypes'}">Typy oznámení</BackButton>
    <ModuleHeader>
      <div id="header-title">
        <h2 v-if="isNew">Přidání typu oznámení</h2>
        <h2 v-else>Úprava typu oznámení</h2>
      </div>
      <v-spacer/>
      <SaveButton class="ml-5 mb-6" :disabled="!isFormValid" :is-saving="isSaving" @save="saveDetail"/>
    </ModuleHeader>
    <div class="mb-5 mt-5">
        <v-form @submit.prevent="saveDetail" v-model="isFormValid" ref="form">
          <div class="v-data-table v-data-table--fixed-header">
            <div class="v-data-table__wrapper">
              <table>
                  <tbody>
                    <tr>
                      <th scope="row" class="table-header">Název</th>
                      <td>
                        <v-text-field
                          :autofocus="isNew"
                          id="type-name"
                          :rules="[required_rule]" 
                          color="dark" 
                          v-model="announcementType.name" 
                          :maxlength="displaysConstants.announcementType.name"
                          :label="`${announcementType.name.length}/${displaysConstants.announcementType.name} znaků`"
                          @change="changeWatch">
                        </v-text-field>
                      </td>
                    </tr>
                    <tr>
                      <th scope="row" class="table-header">Ikona</th>
                      <td>
                        <IconPicker
                          :autofocus="true"
                          id="type-icon"
                          :rules="[required_rule]" 
                          ref="iconPicker" 
                          :initialValue="announcementType.iconName" 
                          v-on:iconChanged="changeIcon">
                        </IconPicker>
                      </td>
                    </tr>
                    <tr>
                      <th scope="row" class="table-header">Barva</th>
                      <td>
                          <v-select
                              id="type-color"
                              :items="colorSelectItems"
                              v-model="announcementType.color"
                              @input="changeWatch">
                              <template v-slot:item="{ props, item }">
                                  <v-list-item v-bind="props">
                                      <template v-slot:prepend>
                                          <v-avatar :color="item.value" size="1em"/>
                                      </template>
                                  </v-list-item>
                              </template>
                              <template v-slot:selection="{ item }">
                                  <v-list-item v-bind="props">
                                      <template v-slot:prepend>
                                          <v-avatar :color="item.value" size="1em"/>
                                      </template>
                                      <v-list-item-title>{{item.title}}</v-list-item-title>
                                  </v-list-item>
                              </template>
                          </v-select>
                      </td>
                    </tr>
                    <tr>
                      <th scope="row" class="table-header">Generující služba <InfoButton right small>Zajišťuje přípravu obsahu dynamicky generovaných oznámení. Pro typy oznámení s ručně definovaným obsahem ji není třeba specifikovat.</InfoButton></th>
                      <td>
                        <v-select
                          id="type-service"
                          color="dark"
                          :items="displaysServices"
                          :disabled="!isNew || isServiceDisable"
                          item-value="id"
                          item-title="name"
                          v-model="announcementType.serviceName"
                          :label="isNew || announcementType.serviceName ? 'Generující služba' : 'Generující služba není nastavena'"
                          :clearable="true"
                          @change="changeWatch">
                        </v-select>
                      </td>
                    </tr>
                    <tr>
                      <th scope="row" class="table-header">Mobilní náhled</th>
                      <td>
                        <v-checkbox
                          id="type-mobile-toggle"
                          v-model="announcementType.displayOnMobile"
                          label="Povolit náhled v mobilní aplikaci My MENDELU?"
                        />
                      </td>
                    </tr>
                    <tr>
                      <th scope="row" class="table-header">Obrazovky <InfoButton right small>Obrazovky, na kterých bude daný typ oznámení zobrazován.</InfoButton></th>
                      <td>
                        <v-select
                            @click="openDisplayDialog = true"
                            id="type-displays"
                            v-model="announcementType.displays"
                            item-title="name"
                            chips
                            label="Obrazovky"
                            readonly
                        >
                        </v-select>
                        <v-dialog
                            v-model="openDisplayDialog"
                            scrollable
                            width="50%"
                        >
                            <v-card title="Obrazovky">
                              <v-card-text>
                                <v-list>
                                  <v-list-item>
                                    <v-list-item-title>
                                      <span class="font-weight-medium">Na výšku</span>
                                    </v-list-item-title>
                                    <v-list-item-subtitle>Jako generující službu lze použít pouze Multimédia na celou obrazovku</v-list-item-subtitle>
                                    <template v-if="getDisplaysPortrait.length > 0" v-slot:append>
                                      <v-checkbox-btn
                                          :model-value="allDisplaysPortraitAdded"
                                          @update:model-value="(value) => addAllPortraitChange(value)"
                                          :disabled="!isPortraitEnable"
                                          :label="allDisplaysPortraitAdded ? 'Odebrat vše' : 'Vybrat vše'"
                                          color="primary"></v-checkbox-btn>
                                    </template>
                                  </v-list-item>
                                  <v-divider></v-divider>
                                  <v-list-item
                                      :key="display.id"
                                      v-for="display in getDisplaysPortrait">
                                    <v-checkbox-btn
                                        :disabled="!isPortraitEnable"
                                        v-model="announcementType.displays"
                                        :value="display"
                                        :label="display.name"
                                        color="primary"></v-checkbox-btn>
                                  </v-list-item>
                                  <v-list-item v-if="getDisplaysPortrait.length === 0">
                                    Žádné dostupné displeje na výšku
                                  </v-list-item>
                                </v-list>
                                <v-list>
                                  <v-list-item>
                                    <v-list-item-title>
                                      <span class="font-weight-medium">Na šířku</span>
                                    </v-list-item-title>
                                    <template v-if="getDisplaysLandscape.length > 0" v-slot:append>
                                      <v-checkbox-btn
                                          :model-value="allDisplaysLandscapeAdded"
                                          :disabled="!isLandscapeEnable"
                                          @update:model-value="value => addAllLandscapeChange(value)"
                                          :label="allDisplaysLandscapeAdded ? 'Odebrat vše' : 'Vybrat vše'"
                                          color="primary"></v-checkbox-btn>
                                    </template>
                                  </v-list-item>
                                  <v-divider></v-divider>
                                  <v-list-item
                                      :key="display.id"
                                      v-for="display in getDisplaysLandscape">
                                    <v-checkbox-btn
                                        :disabled="!isLandscapeEnable"
                                        v-model="announcementType.displays"
                                        :value="display"
                                        :label="display.name"
                                        color="primary"></v-checkbox-btn>
                                  </v-list-item>
                                  <v-list-item v-if="getDisplaysLandscape.length === 0">
                                    Žádné dostupné displeje na šířku
                                  </v-list-item>
                                </v-list>
                              </v-card-text>
                              <v-card-actions>
                                <v-btn
                                    text="Zavřít"
                                    @click="openDisplayDialog = false"
                                ></v-btn>
                              </v-card-actions>
                            </v-card>
                        </v-dialog>
                      </td>
                    </tr>
                  </tbody>
              </table>
            </div>
          </div>
        </v-form>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import {
  displaysMaxValues,
  displaysServices,
  FULL_SCREEN_IMAGE_SERVICE,
  LANDSCAPE,
  mendeluColors,
  PORTRAIT
} from '@/config';
import Loading from "../../../components/Loading";
import IconPicker from "../../../components/icon-picker/IconPicker";
import SaveButton from "../../../components/buttons/SaveButton";
import BackButton from "../../../components/layout/BackButton";
import InfoButton from "../../../components/buttons/InfoButton";
import ModuleHeader from "../../../components/layout/ModuleHeader";
import ItemNotFoundState from '../../../components/states/ItemNotFoundState.vue';

const store = useStore();
const router = useRouter();

const route = useRoute();
const announcementTypeId = computed(() => route.params.announcementTypeId === "new" ? null : Number(route.params.announcementTypeId));

const isSaving = ref(false);
const isFormValid = ref(false);
const openDisplayDialog = ref(false)
const announcementType = reactive({
  name: "",
  color: "#80CC16",
  iconName: "",
  serviceName: undefined,
  displayOnMobile: false,
  displays: []
});

const required_rule = value => !!value || 'Povinné';
const colorSelectItems = computed(() => mendeluColors.map(c => ({ title: c.label, value: c.color })));
const displaysConstants = computed(() => displaysMaxValues);
const isNew = computed(() => announcementTypeId.value === null);

const didLoadData = computed(() => store.getters['announcementTypes/didLoadAnnouncementTypes'] || isNew.value);
const getDisplaysPortrait = computed(() => store.getters['displays/getDisplaysPortrait'])
const getDisplaysLandscape = computed(() => store.getters['displays/getDisplaysLandscape'])
const getDisplays = computed(() => store.getters['displays/getDisplays'])
const objectNotFound = computed(() => store.getters["errors/objectNotFound"])

onMounted(async () => {
  await store.dispatch('displays/fetchDisplays', route.params.unitId);
  if (!isNew.value) {
    await store.dispatch('announcementTypes/fetchAnnouncementTypeById', announcementTypeId.value);
    let type = store.getters['announcementTypes/getAnnouncementTypeById'](announcementTypeId.value);
    type.displays = []
    for (const id of type.displaysIds){
      type.displays.push(getDisplays.value.find(d => d.id === id))
    }
    Object.assign(announcementType, type);
  } else {
    const newType = store.getters['announcementTypes/prepareNewAnnouncementType'];
    Object.assign(announcementType, newType);
    await store.dispatch("errors/clear");
  }
});

watch(() => announcementType, () => {
  store.dispatch('leavePrevent/madeUnsavedChanges')
  if(announcementType.displays[0]?.orientation === PORTRAIT){
    announcementType.serviceName = FULL_SCREEN_IMAGE_SERVICE
  }
}, { deep: true });

const changeIcon = (icon) => {
  announcementType.iconName = icon;
  changeWatch();
};

const changeWatch = () => {
  store.dispatch('leavePrevent/madeUnsavedChanges');
};

const isPortrait = computed(() => announcementType.displays[0]?.orientation === PORTRAIT)

const saveDetail = async () => {
  if (isFormValid.value) {
    isSaving.value = true;
    try {
      if (isNew.value) {
        await store.dispatch('announcementTypes/createAnnouncementType', {
          ...announcementType,
          orientation: isPortrait.value ? PORTRAIT : LANDSCAPE,
          displaysIds: announcementType.displays.map(i => i.id),
          unitId: route.params.unitId
          });
      } else {
        await store.dispatch('announcementTypes/updateAnnouncementType', {
          ...announcementType,
          orientation: isPortrait.value ? PORTRAIT : LANDSCAPE,
          displaysIds: announcementType.displays.map(i => i.id),
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      isSaving.value = false;
      store.dispatch('leavePrevent/didSaveChanges');
      if (isNew.value) {
        router.push({ name: "announcementTypes" });
      }
    }
  }
};

const isPortraitEnable = computed(() => {
  const serviceValid = !announcementType.serviceName || announcementType.serviceName === FULL_SCREEN_IMAGE_SERVICE;
  const isPortrait = announcementType.displays[0]?.orientation === PORTRAIT;
  return serviceValid && (announcementType.displays.length === 0 || isPortrait);
});

const areDisplaysEqual = (isPortrait) => {
  const selectedDisplays = isPortrait ? getDisplaysPortrait.value : getDisplaysLandscape.value;
  const selectedIdsSet = new Set(selectedDisplays.map(d => d.id));
  const announcementIds = announcementType.displays.map(d => d.id);

  return announcementIds.every(id => selectedIdsSet.has(id));
}

const allDisplaysPortraitAdded = computed(() => {
  if(announcementType.displays.length !== getDisplaysPortrait.value.length){
    return false
  } else {
    return areDisplaysEqual(true)
  }
})
const allDisplaysLandscapeAdded = computed(() => {
  if(announcementType.displays.length !== getDisplaysLandscape.value.length){
    return false
  } else {
    return areDisplaysEqual(false)
  }
})

const isLandscapeEnable = computed(() => {
  if(announcementType.displays.length > 0){
    return announcementType.displays[0].orientation === LANDSCAPE;
  } else {
    return true
  }
})

const isServiceDisable = computed(()=>{
  return announcementType.serviceName === FULL_SCREEN_IMAGE_SERVICE && announcementType.displays[0]?.orientation === PORTRAIT
})



const addAllLandscapeChange = (value) => {
  announcementType.displays = []
  if(value) announcementType.displays = [...getDisplaysLandscape.value]
}

const addAllPortraitChange = (value) => {
  announcementType.displays = []
  if(value) announcementType.displays = [...getDisplaysPortrait.value]
}

</script>

<style scoped>
  li {
    list-style-type: none;
  }

  .table-header {
    font-size: 16px;
    width: 15%;
  }
</style>
